import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Calendar, Clock, MapPin, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_ORDER = { Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6, Sunday: 7 };

const CATEGORY_ORDER = ['Development / Recreational', 'Competitive', 'Girls', 'Saturday Training'];
const CATEGORY_COLOR = {
  'Development / Recreational': 'primary',
  'Competitive': 'success',
  'Girls': 'accent',
  'Saturday Training': 'primary',
};

// Convert "HH:MM" -> "H:MM AM/PM"
const to12h = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const displayH = h % 12 || 12;
  return `${displayH}:${String(m).padStart(2, '0')} ${period}`;
};

// Assign a raw Portal session to one of our four marketing buckets
const bucketFor = (s) => {
  const isSat = (s.day_name || DAY_NAMES[s.day_of_week]) === 'Saturday';
  const isFemale = (s.gender || '').toLowerCase() === 'female';
  const isComp = (s.session_type || '').toLowerCase() === 'competitive';
  if (isSat) return 'Saturday Training';
  if (isFemale) return 'Girls';
  if (isComp) return 'Competitive';
  return 'Development / Recreational';
};

export const TrainingScheduleSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [scheduleData, setScheduleData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } },
  };
  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  useEffect(() => {
    const loadSchedule = async () => {
      setLoading(true);
      setError(null);
      try {
        // 1. Fetch all active terms
        const termsRes = await fetch(`${API_URL}/api/academy/terms`);
        if (!termsRes.ok) throw new Error('Failed to load terms');
        const termsData = await termsRes.json();
        const terms = Array.isArray(termsData) ? termsData : (termsData.terms || []);

        // 2. Fetch sessions for each term in parallel
        const perTerm = await Promise.all(
          terms.map(async (t) => {
            const r = await fetch(`${API_URL}/api/academy/sessions/${t.id}`);
            if (!r.ok) return [];
            const d = await r.json();
            return d.templates || d.sessions || (Array.isArray(d) ? d : []);
          })
        );
        const allSessions = perTerm.flat();

        // 3. Dedupe to ONE card per (day, start_time, location) inside each marketing bucket.
        //    (Portal returns the same day/time slot repeated across age groups and session types;
        //     for a marketing view we only care about the unique day/time/location combo.)
        const seen = new Map();
        for (const s of allSessions) {
          const day = s.day_name || DAY_NAMES[s.day_of_week];
          if (!day || !s.start_time) continue;
          const bucket = bucketFor(s);
          const key = `${bucket}|${day}|${s.start_time}|${s.location || ''}`;
          if (!seen.has(key)) {
            seen.set(key, {
              day,
              startTime: s.start_time,
              endTime: s.end_time,
              time: `${to12h(s.start_time)} - ${to12h(s.end_time)}`,
              location: (s.location || '').replace(' Field', ''),
              bucket,
            });
          }
        }

        // 4. Group into buckets
        const grouped = {};
        for (const slot of seen.values()) {
          (grouped[slot.bucket] = grouped[slot.bucket] || []).push(slot);
        }

        // 5. Sort inside each bucket by (day, start_time), and build the final list
        const buckets = CATEGORY_ORDER
          .filter((cat) => grouped[cat] && grouped[cat].length)
          .map((cat) => ({
            category: cat,
            color: CATEGORY_COLOR[cat],
            sessions: grouped[cat].sort((a, b) => {
              const dayDelta = (DAY_ORDER[a.day] || 99) - (DAY_ORDER[b.day] || 99);
              return dayDelta !== 0 ? dayDelta : a.startTime.localeCompare(b.startTime);
            }),
          }));

        setScheduleData(buckets);
      } catch (err) {
        console.error('[Schedule] Failed to load Portal schedule:', err);
        setError('Unable to load training schedule. Please refresh or check back shortly.');
      } finally {
        setLoading(false);
      }
    };
    loadSchedule();
  }, []);

  return (
    <section id="schedule" className="min-h-screen py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="text-center mb-12">
            <Badge variant="outline" className="gold-border text-primary mb-4">
              <Calendar className="h-3 w-3 mr-1" />
              Weekly Sessions
            </Badge>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gradient-gold mb-4">
              Training Schedule
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-base">
              All sessions are 1.5 hours
            </p>
          </motion.div>

          {loading && (
            <div className="flex justify-center items-center gap-2 py-16 text-muted-foreground" data-testid="schedule-loading">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading live schedule from Portal…
            </div>
          )}

          {error && !loading && (
            <div className="max-w-lg mx-auto p-4 rounded bg-red-500/10 border border-red-500/30 text-sm text-red-300 text-center" data-testid="schedule-error">
              {error}
            </div>
          )}

          {!loading && !error && scheduleData.length === 0 && (
            <div className="max-w-lg mx-auto p-4 rounded bg-amber-500/10 border border-amber-500/30 text-sm text-amber-300 text-center" data-testid="schedule-empty">
              No active sessions found in Portal. Check back once terms are published.
            </div>
          )}

          {/* Schedule Cards */}
          {!loading && !error && scheduleData.length > 0 && (
            <div className="grid md:grid-cols-3 gap-6" data-testid="schedule-grid">
              {scheduleData.map((category, idx) => (
                <motion.div key={idx} variants={itemVariants}>
                  <Card className={`bg-gradient-card gold-border h-full ${
                    category.color === 'success' ? 'ring-1 ring-success/30' : ''
                  }`}>
                    <CardHeader className="pb-4">
                      <CardTitle className={`font-display text-xl ${
                        category.color === 'success' ? 'text-success' : 'text-primary'
                      }`}>
                        {category.category}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {category.sessions.map((session, sIdx) => (
                          <div key={sIdx} className="glass-card rounded-lg p-3 border border-border">
                            <div className="flex items-center gap-2 mb-2">
                              <Calendar className="h-4 w-4 text-primary" />
                              <span className="text-card-foreground font-medium text-sm">
                                {session.day}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 mb-1">
                              <Clock className="h-4 w-4 text-muted-foreground" />
                              <span className="text-muted-foreground text-sm">
                                {session.time}
                              </span>
                            </div>
                            {session.location && (
                              <div className="flex items-center gap-2">
                                <MapPin className="h-4 w-4 text-muted-foreground" />
                                <span className="text-muted-foreground text-sm">
                                  {session.location}
                                </span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}

          {/* Note */}
          <motion.div variants={itemVariants} className="mt-8">
            <div className="glass-card rounded-lg p-4 gold-border text-center">
              <p className="text-muted-foreground text-sm">
                Schedule may vary during school holidays and public holidays
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default TrainingScheduleSection;
